import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Sparkles, ScanLine, Search, Check, AlertTriangle, Package, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { aiLookupBarcode, createProduct, formatMXN } from "@/lib/api";

const SAMPLE_BARCODES = [
  { code: "7501055300075", label: "Coca-Cola lata" },
  { code: "7501030459286", label: "Pan Bimbo" },
  { code: "7501020544428", label: "Corona 355" },
  { code: "7501008445150", label: "Sabritas" },
  { code: "7501000112036", label: "Maseca 1kg" },
  { code: "7501020505036", label: "Leche Lala" },
];

export default function AIProductModal({ open, onClose, onCreated }) {
  const [barcode, setBarcode] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [stock, setStock] = useState("");
  const [salePrice, setSalePrice] = useState("");
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setBarcode(""); setResult(null); setStock(""); setSalePrice("");
      setTimeout(() => inputRef.current?.focus(), 250);
    }
  }, [open]);

  const doLookup = async (codeOverride) => {
    const code = (codeOverride || barcode || "").trim();
    if (!code) return toast.error("Escanea o escribe un código de barras");
    setLoading(true); setResult(null);
    try {
      const r = await aiLookupBarcode(code);
      setResult(r);
      if (r.found) {
        setSalePrice(String(r.suggested_sale_price ?? ""));
        setTimeout(() => document.getElementById("ai-stock-input")?.focus(), 100);
      }
    } catch (e) {
      toast.error("No pudimos consultar el catálogo. Intenta de nuevo.");
    } finally { setLoading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!result?.found) return;
    if (!stock || Number(stock) < 0) return toast.error("Escribe cuántas piezas tienes");
    if (!salePrice || Number(salePrice) <= 0) return toast.error("Confirma el precio de venta");
    setSaving(true);
    try {
      const created = await createProduct({
        name: result.name,
        category: result.category,
        wholesale_provider: result.wholesale_provider,
        sale_price: Number(salePrice),
        cost_price: 0,
        stock_quantity: Number(stock),
        min_stock_alert: 0,
        barcode: result.barcode,
        unit: result.unit || "pza",
      });
      toast.success(`${created.name} agregado con IA`);
      onCreated?.();
      onClose?.();
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "No se pudo guardar el producto");
    } finally { setSaving(false); }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 bg-black/75 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={onClose}
          data-testid="ai-modal"
        >
          <motion.div
            initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 30, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-3xl bg-[#0a0a0a] border border-white/10 rounded-3xl p-6 md:p-10"
          >
            <button onClick={onClose} className="absolute top-4 right-4 text-white/50 hover:text-white p-2" data-testid="ai-close">
              <X size={18} />
            </button>

            <div className="chip mb-4">
              <Sparkles size={12} className="text-[#ccff00]" /> Agregar con IA
            </div>
            <h2 className="font-display text-3xl md:text-4xl tracking-tighter leading-none">
              Escanea o escribe el <span className="text-[#ccff00]">código</span>.
              <br className="hidden md:block" /> CHALANN llena el resto.
            </h2>
            <p className="mt-3 text-white/60 text-sm max-w-xl">
              Nuestro catálogo mexicano identifica más de 12,000 productos. Tú solo confirmas <em className="text-white not-italic">precio</em> y <em className="text-white not-italic">existencias</em>.
            </p>

            {/* Barcode input */}
            <div className="mt-6 rounded-2xl border border-white/10 bg-black/40 p-3 md:p-4">
              <div className="flex items-center gap-3">
                <ScanLine size={20} className="text-[#ccff00] flex-shrink-0" />
                <input
                  ref={inputRef}
                  value={barcode}
                  onChange={(e) => setBarcode(e.target.value.replace(/\s/g, ""))}
                  onKeyDown={(e) => e.key === "Enter" && doLookup()}
                  placeholder="7501055300075"
                  inputMode="numeric"
                  className="flex-1 bg-transparent outline-none font-mono text-base md:text-lg tracking-wider placeholder-white/25"
                  data-testid="ai-barcode-input"
                />
                <button
                  onClick={() => doLookup()}
                  disabled={loading}
                  className="btn-primary text-sm disabled:opacity-60"
                  data-testid="ai-lookup-btn"
                >
                  {loading ? "Buscando…" : (<><Search size={14} /> Consultar IA</>)}
                </button>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                <span className="font-mono text-[10px] tracking-widest text-white/40 uppercase self-center mr-1">Simular escaneo:</span>
                {SAMPLE_BARCODES.map((b) => (
                  <button
                    key={b.code}
                    onClick={() => { setBarcode(b.code); doLookup(b.code); }}
                    className="chip hover:border-[#ccff00] hover:text-[#ccff00] transition-colors"
                    data-testid={`ai-sample-${b.code}`}
                  >
                    {b.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Result */}
            <div className="mt-6 min-h-[220px]">
              {loading && <ScanningState />}
              {!loading && !result && <EmptyPreview />}
              {!loading && result && !result.found && <NotFoundState result={result} />}
              {!loading && result && result.found && (
                <FoundPreview
                  result={result}
                  stock={stock} setStock={setStock}
                  salePrice={salePrice} setSalePrice={setSalePrice}
                  submit={submit} saving={saving}
                />
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function ScanningState() {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-8 flex flex-col items-center gap-4" data-testid="ai-scanning">
      <div className="relative w-16 h-16">
        <motion.div
          className="absolute inset-0 rounded-full border-2 border-[#ccff00]/30 border-t-[#ccff00]"
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
        />
        <Sparkles size={22} className="text-[#ccff00] absolute inset-0 m-auto" />
      </div>
      <p className="font-mono text-[11px] tracking-widest text-white/60 uppercase">Consultando catálogo IA…</p>
    </div>
  );
}

function EmptyPreview() {
  return (
    <div className="rounded-2xl border border-dashed border-white/10 p-8 text-center text-white/40 text-sm" data-testid="ai-empty">
      Escanea un código o toca uno de los ejemplos arriba para probar.
    </div>
  );
}

function NotFoundState({ result }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6" data-testid="ai-notfound">
      <div className="flex items-start gap-3">
        <AlertTriangle size={22} className="text-[#ff5a00] flex-shrink-0 mt-1" />
        <div>
          <p className="font-display text-xl tracking-tight">No lo tengo en el catálogo… todavía.</p>
          <p className="text-white/60 text-sm mt-2">{result.hint}</p>
          {result.source && (
            <p className="mt-3 font-mono text-[10px] tracking-widest text-white/40 uppercase">
              Confianza: {result.confidence} · {result.source}
            </p>
          )}
          <p className="mt-4 text-sm text-white/70">
            Usa <strong>&ldquo;Nuevo producto&rdquo;</strong> para capturarlo a mano — CHALANN aprenderá para la próxima.
          </p>
        </div>
      </div>
    </div>
  );
}

function FoundPreview({ result, stock, setStock, salePrice, setSalePrice, submit, saving }) {
  const already = result.already_in_inventory;
  return (
    <motion.form
      onSubmit={submit}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-2xl border border-[#ccff00]/40 bg-[#ccff00]/[0.06] p-6"
      data-testid="ai-found"
    >
      <div className="flex items-center gap-2 mb-4">
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#ccff00] text-black text-[10px] font-mono uppercase tracking-widest font-bold">
          <Sparkles size={11} /> Identificado por IA
        </span>
        <span className="font-mono text-[10px] tracking-widest text-white/50 uppercase">
          Confianza: {result.confidence}
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 md:gap-6">
        <div className="md:col-span-3 space-y-3">
          <div>
            <p className="font-mono text-[10px] tracking-widest text-white/40 uppercase mb-1">Producto</p>
            <p className="font-display text-2xl md:text-3xl tracking-tighter leading-tight" data-testid="ai-name">
              {result.name}
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <ReadOnlyField label="Categoría" value={result.category} testid="ai-category" />
            <ReadOnlyField label="Proveedor" value={result.wholesale_provider} testid="ai-provider" />
            <ReadOnlyField label="Unidad" value={result.unit || "pza"} testid="ai-unit" />
            <ReadOnlyField label="Código" value={result.barcode} mono testid="ai-barcode" />
          </div>
          <p className="font-mono text-[10px] tracking-widest text-white/40 uppercase pt-2 border-t border-white/10">
            fuente: {result.source}
          </p>

          {already && (
            <div className="mt-2 rounded-xl border border-[#ff5a00]/40 bg-[#ff5a00]/[0.08] p-3 text-sm">
              <p className="text-[#ff5a00] font-medium">Ya está en tu inventario</p>
              <p className="text-white/70 text-xs mt-1">
                Actualmente tienes {already.stock_quantity} pz. Si continúas se creará una entrada nueva; mejor edítalo desde la lista.
              </p>
            </div>
          )}
        </div>

        <div className="md:col-span-2 space-y-3">
          <div>
            <label className="font-mono text-[10px] tracking-widest text-white/40 uppercase">Precio de venta MXN</label>
            <div className="mt-1 flex items-baseline gap-2">
              <input
                type="number"
                step="0.5"
                value={salePrice}
                onChange={(e) => setSalePrice(e.target.value)}
                className="flex-1 bg-black/50 border border-white/10 rounded-xl px-4 py-3 font-display text-2xl tracking-tighter text-[#ccff00] focus:outline-none focus:border-[#ccff00]"
                data-testid="ai-sale-price"
              />
            </div>
            <p className="mt-1 text-[11px] text-white/40">
              Sugerido por IA: <span className="text-white/80">{formatMXN(result.suggested_sale_price)}</span> (promedio miscelánea MX)
            </p>
          </div>

          <div>
            <label className="font-mono text-[10px] tracking-widest text-white/40 uppercase">
              ¿Cuántos tienes hoy? <span className="text-[#ff5a00]">*</span>
            </label>
            <input
              id="ai-stock-input"
              type="number"
              step="1"
              value={stock}
              onChange={(e) => setStock(e.target.value)}
              placeholder="24"
              autoFocus
              className="w-full mt-1 bg-black/50 border border-white/10 rounded-xl px-4 py-3 font-display text-2xl tracking-tighter focus:outline-none focus:border-[#ff5a00]"
              data-testid="ai-stock"
            />
            <p className="mt-1 text-[11px] text-white/40">Es lo único que necesitamos de ti.</p>
          </div>

          <button
            type="submit"
            disabled={saving}
            className="btn-primary w-full justify-center text-base disabled:opacity-60"
            data-testid="ai-save"
          >
            {saving ? "Guardando…" : (<><Check size={16} /> Guardar producto <ArrowRight size={14} /></>)}
          </button>
        </div>
      </div>
    </motion.form>
  );
}

function ReadOnlyField({ label, value, mono, testid }) {
  return (
    <div>
      <p className="font-mono text-[10px] tracking-widest text-white/40 uppercase">{label}</p>
      <p
        className={`mt-1 text-sm text-white/90 ${mono ? "font-mono" : ""} bg-black/30 border border-white/5 rounded-lg px-3 py-2 truncate`}
        data-testid={testid}
      >
        {value || "—"}
      </p>
    </div>
  );
}
